import { render, html } from "lit";
import { ColumnSource, RequestFilters, RowSource, CellModel } from "@omegagrid/grid";
import { ColorTypes, IconSpec, dom, actions } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { UserTreeAdapter } from "@reporting/rp-mf-users";
import { AccountingTreeAdapter } from "@reporting/rp-mf-accounting";
import { AccountingRights } from "@reporting/rp-types/core";
import { ModuleCode, MODULE_CODES } from "@reporting/rp-types/accounting";
import { ReportContext } from "@reporting/rp-types/analytics";
import { ModuleDefinition } from '@reporting/rp-types/definition'
import { EntityAccessType } from "@reporting/rp-types/users";

const PAGE_SIZE = 500;

const additionalModuleIconMap: { [key in ModuleCode]?: IconSpec } = {
	[MODULE_CODES.CUSTOM]: { icon: 'shapes', color: `var(--og-colortype-${ColorTypes.Yellow}-border-color)` },
	[MODULE_CODES.DASHBOARD]: { icon: 'cubes', color: `var(--og-colortype-${ColorTypes.Accent}-border-color)` },
	[MODULE_CODES.SPREADSHEET]: { icon: 'table', color: `var(--og-colortype-${ColorTypes.Gray}-border-color)` },
};

export class ReportGridEditorAdapter extends GridEditorAdapter {
	private _columns: ColumnSource[];
	private _groupModules: ModuleDefinition[];

	attached() {
		dom.appendStyle(this.component.table.shadowRoot, `
			a { color: var(--og-accent-color) !important; }
			a:hover { text-decoration: underline; cursor: pointer; }
		`, '__rp-admin-report-grid-style');
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._groupModules = await rp.ms('definition').get<ModuleDefinition[]>('groupModules');
		const modulesList = [
			...this._groupModules.map(m => ({ key: m.module, value: m.name, icon: this.getModuleIconSpec(m.module as ModuleCode) })),
			{ key: MODULE_CODES.CUSTOM, value: msg('Custom'), icon: additionalModuleIconMap[MODULE_CODES.CUSTOM] },
			{ key: MODULE_CODES.DASHBOARD, value: msg('Dashboard'), icon: additionalModuleIconMap[MODULE_CODES.DASHBOARD] },
			{ key: MODULE_CODES.SPREADSHEET, value: msg('Spreadsheet'), icon: additionalModuleIconMap[MODULE_CODES.SPREADSHEET] },
		];
		this._columns = [{
			t: 'a',
			e: false,
			w: 5 + 20 * 4,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red,
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow
			}, {
				action: 'access',
				title: msg('Access'),
				icon: 'shield-check',
				color: ColorTypes.Gray
			}, {
				action: 'open',
				title: msg('Open'),
				icon: 'book-open',
				color: ColorTypes.Blue
			}],
			filter: false,
			sorting: false,
		}, {
			data: 'id_context',
			header: '#',
			t: 't',
			w: 100,
			e: false,
		}, {
			data: '_module',
			header: msg('Module'),
			t: 's',
			w: 130,
			e: false,
			content: modulesList,
			renderer: this.moduleRenderer,
		}, {
			data: 'accounting',
			header: msg('Company'),
			t: 's',
			w: 200,
			e: true,
			adapter: new AccountingTreeAdapter({
				idGroup: rp.accountingProvider.accounting.id_accounting_group,
				right: AccountingRights.Admin,
				treeOptions: { selectMode: 'single', autoClose: true, search: true }
			}),
		}, {
			data: 'user_name',
			header: msg('User'),
			t: 's',
			w: 150,
			e: true,
			adapter: new UserTreeAdapter(false, { selectMode: 'single', autoClose: true, search: true })
		}, {
			data: 'name',
			header: msg('Name'),
			t: 't',
			w: 200,
			e: true
		}, {
			data: 'alias',
			header: msg('Alias'),
			t: 't',
			w: 200,
			e: true
		}, {
			data: 'remark',
			header: msg('Remark'),
			t: 't',
			w: 200,
			e: true
		}, {
			data: 'id_embedding',
			header: msg('Embedding'),
			t: 't',
			w: 250,
			e: false,
			renderer: this.embeddingRenderer,
		}, {
			data: 'who_created',
			header: msg('Created by'),
			t: 't',
			w: 180,
			e: false
		}, {
			data: 'date_created',
			header: msg('Created'),
			t: 'dt',
			w: 145,
			e: false
		}, {
			data: 'idz',
			t: 'n',
			e: false,
			hidden: true,
		}, {
			data: 'module',
			t: 't',
			e: true,
			hidden: true,
		}, {
			data: '_id_context',
			t: 'n',
			e: true,
			hidden: true,
		}, {
			data: '_orig_accounting',
			t: 'n',
			e: true,
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const reports = await rp.ms('analytics').get<ReportContext[]>(`/groupContexts`, this.createRequestParams(filters,
			{
				count: PAGE_SIZE,
				fields: ['idz', 'id_context', 'module', 'accounting', 'company', 'user_name', 'login_name', 'name', 'alias', 'remark', 'id_embedding', 'who_created', 'date_created']
			}, { _module: 'module' }
		));
		return reports.map(data => ({ data: {
			...data,
			_module: data.module,
			accounting: { v: data.accounting, dv: `${data.accounting} ${data.company}` },
			user_name: { v: data.user_name, dv: data.login_name },
			_id_context: data.id_context,
			_orig_accounting: data.accounting,
		}}));
	}

	embeddingRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';

		render(html`
			<a @pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
				@click="${() => actions.dispatchActionEvent(this.editor, 'editor', {
				key: 'click',
				value: cell.value,
			})}">${cell.value}</a>
		`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	moduleRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.justifyContent = 'center';
		innerDiv.style.alignItems = 'center';

		const iconSpec = this.getModuleIconSpec(cell.value as ModuleCode);
		render(html`
			<span style="display: block; text-align: center;">
				<og-icon icon="${iconSpec?.icon}" style="color:${iconSpec?.color}"></og-icon>
			</span>
		`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	getModuleIconSpec(m: ModuleCode): IconSpec {
		const module = this._groupModules.find(mod => mod.module === m);
		let icon: IconSpec;
		switch (m) {
			case MODULE_CODES.CUSTOM:
				icon = additionalModuleIconMap[MODULE_CODES.CUSTOM];
				break;
			case MODULE_CODES.DASHBOARD:
				icon = additionalModuleIconMap[MODULE_CODES.DASHBOARD];
				break;
			case MODULE_CODES.SPREADSHEET:
				icon = additionalModuleIconMap[MODULE_CODES.SPREADSHEET];
				break;
			default:
				icon = { icon: module ? 'cube' : 'question', color: module?.color || `var(--og-colortype-${ColorTypes.Gray}-border-color)` };
		}
		return icon;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			refreshAfterSave: true,
			toolbar: {
				new: {enabled: false},
			},
			rowMenu: {
				deleteRows: true,
			},
		});
	}

	async save(rowData: RowData[]) {
		const data = rowData.map(row => Object.assign({_state: row.state, _index: row.index}, row.data));
		for (const d of data) {
			switch (d._state) {
				case 'new':
					await rp.ms('analytics').post(`/context/${d._id_context}`, d, { duplicate: true, accounting: d._orig_accounting as number });
					break;
				case 'edited':
					await rp.ms('analytics').put(`/context/${d.id_context}`, d, { accounting: d._orig_accounting as number });
					break;
				case 'deleted':
					await rp.ms('analytics').delete(`/context/${d.idz}`, { accounting: d._orig_accounting as number });
					break;
			}
		}
	}

	async onAction(action: string, cell: CellModel) {
		const data = cell.row.data as ReportContext;
		switch(action) {
			case 'access':
				rp.host.open(`users-entityUsers-${EntityAccessType.Context}-${data.id_context}`, { title: `${msg('Access')}: ${data.name}` });
				break;
			case 'open':
				rp.host.open(`analytics-reportContainer-${data.id_context}`, { title: data.name });
				break;
		}
	}

}
